import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../lib/config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveIcon from '@mui/icons-material/Remove';
import PendingIcon from '@mui/icons-material/Pending';

interface UserPick {
  id: number;
  week: number;
  home_team: string;
  away_team: string;
  home_team_abbr: string;
  away_team_abbr: string;
  home_spread: number;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  game_date: string;
  pick_value: string;
  result?: 'win' | 'lose' | 'push';
}

interface UserDetailProps {
  userId: string;
  username: string;
  displayName: string;
  season: number;
  onClose: () => void;
}

const UserDetail: React.FC<UserDetailProps> = ({ userId, username, displayName, season, onClose }) => {
  const [picks, setPicks] = useState<UserPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWeek, setSelectedWeek] = useState<string>('all');

  useEffect(() => {
    fetchUserPicks();
  }, [userId, season]);

  const fetchUserPicks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/user-picks/${userId}/${season}`);
      if (!response.ok) throw new Error('Failed to fetch user picks');
      
      const data = await response.json();
      setPicks(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateResult = (pick: UserPick): 'win' | 'lose' | 'push' | 'pending' => {
    if (!pick.is_final || pick.home_score === undefined || pick.away_score === undefined) {
      return 'pending';
    }

    const homeScoreWithSpread = pick.home_score + pick.home_spread;
    
    if (pick.pick_value === 'home') {
      if (homeScoreWithSpread > pick.away_score) return 'win';
      if (homeScoreWithSpread === pick.away_score) return 'push';
      return 'lose';
    } else {
      if (pick.away_score > homeScoreWithSpread) return 'win';
      if (pick.away_score === homeScoreWithSpread) return 'push';
      return 'lose';
    }
  };

  const getSpreadDisplay = (spread: number) => {
    return spread > 0 ? `+${spread}` : spread.toString();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getResultIcon = (result: 'win' | 'lose' | 'push' | 'pending') => {
    switch (result) {
      case 'win':
        return <CheckCircleIcon color="success" />;
      case 'lose':
        return <CancelIcon color="error" />;
      case 'push':
        return <RemoveIcon color="disabled" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return null;
    }
  };

  const getResultColor = (result: 'win' | 'lose' | 'push' | 'pending') => {
    switch (result) {
      case 'win':
        return 'success';
      case 'lose':
        return 'error';
      case 'push':
        return 'default';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredPicks = selectedWeek === 'all' 
    ? picks 
    : picks.filter(pick => pick.week === parseInt(selectedWeek));

  const picksByWeek = filteredPicks.reduce((acc, pick) => {
    if (!acc[pick.week]) acc[pick.week] = [];
    acc[pick.week].push(pick);
    return acc;
  }, {} as { [week: number]: UserPick[] });

  const availableWeeks = Array.from(new Set(picks.map(pick => pick.week))).sort((a, b) => a - b);

  const getWeekStats = (weekPicks: UserPick[]) => {
    const stats = { wins: 0, losses: 0, pushes: 0, pending: 0 };
    weekPicks.forEach(pick => {
      const result = calculateResult(pick);
      if (result === 'win') stats.wins++;
      else if (result === 'lose') stats.losses++;
      else if (result === 'push') stats.pushes++;
      else stats.pending++;
    });
    return stats;
  };

  if (loading) {
    return (
      <Dialog open={true} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={true} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">Error: {error}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h1">
              🏈 {displayName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              @{username} - {season} Season
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3} p={2}>
          <FormControl fullWidth variant="outlined" size="medium">
            <InputLabel>Filter by Week</InputLabel>
            <Select
              value={selectedWeek}
              label="Filter by Week"
              onChange={(e) => setSelectedWeek(e.target.value as string)}
            >
              <MenuItem value="all">All Weeks</MenuItem>
              {availableWeeks.map(week => (
                <MenuItem key={week} value={week}>Week {week}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {Object.keys(picksByWeek).length === 0 ? (
          <Box textAlign="center" py={4}>
            <SportsFootballIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No picks found for this user.
            </Typography>
          </Box>
        ) : (
          Object.entries(picksByWeek)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([week, weekPicks]) => {
              const stats = getWeekStats(weekPicks);
              return (
                <Accordion key={week} defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={2}>
                      <Typography variant="h6">
                        Week {week}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Chip label={`${stats.wins}W`} color="success" size="small" variant="outlined" />
                        <Chip label={`${stats.losses}L`} color="error" size="small" variant="outlined" />
                        <Chip label={`${stats.pushes}P`} color="default" size="small" variant="outlined" />
                        {stats.pending > 0 && (
                          <Chip label={`${stats.pending} Pending`} color="warning" size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Game</strong></TableCell>
                            <TableCell align="center"><strong>Pick</strong></TableCell>
                            <TableCell align="center"><strong>Score</strong></TableCell>
                            <TableCell align="center"><strong>Result</strong></TableCell>
                            <TableCell align="center"><strong>Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weekPicks
                            .sort((a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime())
                            .map(pick => {
                              const result = calculateResult(pick);
                              return (
                                <TableRow 
                                  key={pick.id}
                                  sx={{
                                    backgroundColor: result === 'win' ? 'rgba(76, 175, 80, 0.08)' : 
                                                   result === 'lose' ? 'rgba(244, 67, 54, 0.08)' : 
                                                   result === 'push' ? 'rgba(158, 158, 158, 0.08)' : 'inherit'
                                  }}
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {pick.away_team_abbr} @ {pick.home_team_abbr}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Spread: {pick.away_team_abbr} {getSpreadDisplay(-pick.home_spread)}, {pick.home_team_abbr} {getSpreadDisplay(pick.home_spread)}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={`${pick.pick_value === 'home' ? pick.home_team_abbr : pick.away_team_abbr} (${pick.pick_value === 'home' ? getSpreadDisplay(pick.home_spread) : getSpreadDisplay(-pick.home_spread)})`}
                                      color={pick.pick_value === 'home' ? 'primary' : 'secondary'}
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    {pick.is_final && pick.home_score !== undefined && pick.away_score !== undefined ? (
                                      <Box>
                                        <Typography variant="body2">
                                          {pick.away_team_abbr}: {pick.away_score}
                                        </Typography>
                                        <Typography variant="body2">
                                          {pick.home_team_abbr}: {pick.home_score}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                      {getResultIcon(result)}
                                      <Chip
                                        label={
                                          result === 'win' ? 'WIN' :
                                          result === 'lose' ? 'LOSE' :
                                          result === 'push' ? 'PUSH' :
                                          'Pending'
                                        }
                                        color={getResultColor(result) as any}
                                        variant="filled"
                                        size="small"
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="caption">
                                      {formatDate(pick.game_date)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserDetail;